import { FC } from 'react';
import { DmIconSubscribe, DmIconPur, DmIconRedeem } from '../icon/dmIcon';
import css from './index.module.scss';
import EfModal from '@/components/UI/Modal';
import PolicyCheck from '@/components/PolicyCheck';
import { useGlobal } from '@/utils/useGlobal';
import { emailMap } from '@/constants/delta2max';
import Mailchimp from '@/components/SignUpForm/Mailchimp';
import MailchimpView from '@/components/SignUpForm/Mailchimp/BaseFormView';
import Klaviyo from '@/components/SignUpForm/Klaviyo';
import KlaviyoView from '@/components/SignUpForm/Klaviyo/BaseFormView';
import Popup from '@/components/SignUpForm/Popup';

export const Z1_A: FC = () => {
    const { useLang, useRouter, useState, gtm } = useGlobal();
    const { t } = useLang('delta2max.teaser');
    const { locale, query } = useRouter();
    const emailConf = emailMap[locale] || emailMap.us;
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [popupVisible, setPopupVisible] = useState(false);
    const [checked, setChecked] = useState(false);

    const EU_SITE = ['eu', 'uk', 'de', 'fr', 'it', 'es'];

    const onCheckChange = (e: any) => {
        if (e.target.checked) {
            setChecked(true);
        } else {
            setChecked(false);
        }
    };

    return (
        <div className={`${css.Z1_AComp} GtmModuleData-section`} data-section-id={css.Z1_AComp} data-section-title={t('subscribe_title')}>
            <div className="subscribe-title">{t('subscribe_title')}</div>
            <div className="subscribe-desc">{t('subscribe_desc')}</div>
            <div className="intro-box">
                <div className="step-box">
                    <div className="time">{t('subscribe_step1_date')}</div>
                    <div className="icon">
                        <DmIconSubscribe />
                    </div>
                    <div className="title">{t('subscribe_step1_title')}</div>
                    <div className="desc">{t('subscribe_step1_desc')}</div>
                </div>
                <div className="step-box">
                    <div className="time">{t('subscribe_step2_date')}</div>
                    <div className="icon">
                        <DmIconPur />
                    </div>
                    <div className="title">{t('subscribe_step2_title')}</div>
                    <div className="desc">{t('subscribe_step2_desc')}</div>
                </div>
                <div className="step-box">
                    <div className="time">{t('subscribe_step3_date')}</div>
                    <div className="icon">
                        <DmIconRedeem />
                    </div>
                    <div className="title">{t('subscribe_step3_title')}</div>
                    <div className="desc">{t('subscribe_step3_desc')}</div>
                </div>
                <div className="step-line"></div>
                <div className="step-line step-line-2"></div>
            </div>
            <div className="email-box-wrapper">
                <div className="email-desc">
                    {t.rich('subscribe_more_details', {
                        link: (text: string) => (
                            <span
                                className="more-link"
                                onClick={() => {
                                    gtm.push({
                                        event: 'uaEvent',
                                        event_name: 'activity_details',
                                        button_name: text,
                                    });
                                    setDetailModalVisible(true);
                                }}
                            >
                                {text}
                            </span>
                        ),
                    })}
                </div>
                <div className="email-box email-box-subscribe">
                    {emailConf?.type === 'klaviyo' && (
                        <Klaviyo
                            extra_properties={{
                                custom_source: emailConf.source,
                                $source: emailConf.source,
                                $method_type: 'Klaviyo Form',
                                $consent_version: 'Embed default text',
                                $consent_form_id: emailConf.consent_form_id,
                            }}
                            successCallback={() => {
                                setPopupVisible(true);
                                try {
                                    gtm.push({
                                        event: 'uaEvent',
                                        event_name: 'activity_submit',
                                        button_name: t('sec1_sign'),
                                        status: 'success',
                                        tag: 'Agree',
                                    });
                                    window?.fbq && window?.fbq('track', 'Lead');
                                } catch (e) {
                                    console.error(e);
                                }
                            }}
                            errorCallback={() => {
                                gtm.push({
                                    event: 'uaEvent',
                                    event_name: 'activity_submit',
                                    button_name: t('sec1_sign'),
                                    status: 'fail',
                                    tag: 'Agree',
                                });
                            }}
                        >
                            <KlaviyoView
                                list_id={emailConf.list_id}
                                submitText={t('subscribe_step1_title')}
                                placeholder={t('sec1_placeholder')}
                                disabled={!checked}
                            />
                        </Klaviyo>
                    )}
                    {emailConf.type === 'mailchimp' && (
                        <>
                            <Mailchimp
                                successCallback={() => {
                                    setPopupVisible(true);
                                    try {
                                        gtm.push({
                                            event: 'uaEvent',
                                            event_name: 'activity_submit',
                                            button_name: t('sec1_sign'),
                                            status: 'success',
                                            tag: 'Agree',
                                        });
                                        window?.fbq('track', 'Lead');
                                    } catch (e) {
                                        console.error(e);
                                    }
                                }}
                                errorCallback={() => {
                                    gtm.push({
                                        event: 'uaEvent',
                                        event_name: 'activity_submit',
                                        button_name: t('sec1_sign'),
                                        status: 'fail',
                                        tag: 'Agree',
                                    });
                                }}
                            >
                                <MailchimpView
                                    action={emailConf.action}
                                    extraFields={emailConf.extraFields}
                                    submitText={t('subscribe_step1_title')}
                                    placeholder={t('sec1_placeholder')}
                                    disabled={!checked}
                                />
                            </Mailchimp>
                        </>
                    )}
                </div>
                <PolicyCheck onCheckChange={onCheckChange} />
            </div>
            <EfModal visible={detailModalVisible} className={css.detailModal} width={924} onCancel={() => setDetailModalVisible(false)}>
                <div className="content-box">
                    <div className="title">{t('subscribe_more_details_title')}</div>
                    <div className="text-box">
                        <div className="num">1.</div>
                        <div className="text">{t('subscribe_more_details_text1')}</div>
                    </div>
                    <div className="text-box">
                        <div className="num">2.</div>
                        <div className="text">{t('subscribe_more_details_text2')}</div>
                    </div>
                    <div className="text-box">
                        <div className="num">3.</div>
                        <div className="text">{t('subscribe_more_details_text3')}</div>
                    </div>
                    <div className="text-box">
                        <div className="num">4.</div>
                        <div className="text">{t('subscribe_more_details_text4')}</div>
                    </div>
                    <div className="text-box">
                        <div className="num">5.</div>
                        <div className="text">{t('subscribe_more_details_text5')}</div>
                    </div>
                    <div className="text-box">
                        <div className="num">6.</div>
                        <div className="text">{t('subscribe_more_details_text6')}</div>
                    </div>
                    <div className="text-box">
                        <div className="num">7.</div>
                        <div className="text">{t('subscribe_more_details_text7')}</div>
                    </div>
                </div>
            </EfModal>
            <Popup
                visible={popupVisible}
                title={t('sec1_popup_title')}
                content={t('sec1_popup_desc')}
                subtitle={t('sec1_popup_title2')}
                buttonText={t('sec1_popup_btn')}
                onCancel={() => setPopupVisible(false)}
                btnLink={`${emailConf.popupBtnLink}${query?.cid ? `?cid=${query.cid}` : ''}`}
                event_name={EU_SITE.includes(locale) ? 'activity_join_login' : 'activity_join'}
            />
            <style jsx global>
                {`
                    .ant-modal-content {
                        background: #fff !important;
                    }
                    .ant-modal-content .popup-title {
                        color: #03060b !important;
                    }
                    .ant-modal-content .popup-btn {
                        padding: 0 97.5px;
                    }
                    .ant-modal-content .popup-btn:hover {
                        background: #1e5ef4;
                    }
                    .ant-modal-content .ant-modal-close {
                        right: 30px;
                        left: auto;
                        width: 40px;
                        height: 40px;
                    }
                    .ant-modal-content .ant-modal-close .ant-modal-close-x {
                        width: 40px;
                        height: 40px;
                        font-size: 18px;
                        background: #999;
                        border: 0;
                        color: #fff;
                    }
                    .ant-modal-content .ant-modal-close .ant-modal-close-x:hover {
                        background: #666;
                    }
                `}
            </style>
        </div>
    );
};
